import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Flame, Loader2, ArrowRight } from "lucide-react";

function digits(v) { return (v || "").replace(/\D/g, ""); }
function maskPhone(v) {
  const d = digits(v).slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

export default function PSSPublicRegister() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [proc, setProc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", community: "", phone: "" });

  useEffect(() => {
    fetch(`${API}/pss/processes/${code}`).then((r) => {
      if (!r.ok) throw new Error("Processo não encontrado ou inativo.");
      return r.json();
    }).then((d) => {
      setProc(d);
      if (!d.self_register_enabled) {
        toast.error("Este processo não permite auto-cadastro público.");
      }
      // pré-seleciona a única comunidade se houver só uma
      if (Array.isArray(d.communities) && d.communities.length === 1) {
        setForm((f) => ({ ...f, community: d.communities[0] }));
      }
    }).catch((e) => toast.error(e.message)).finally(() => setLoading(false));
  }, [code]);

  const submit = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    if (name.split(/\s+/).length < 2) return toast.error("Informe seu nome completo.");
    if (!form.community.trim()) return toast.error("Selecione sua comunidade.");
    if (digits(form.phone).length < 10) return toast.error("Telefone inválido. Ex.: (93) 99999-9999");
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/pss/auto-cadastro/${code}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, community: form.community.trim(), phone: form.phone }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({ detail: "Erro" }));
        throw new Error(typeof j.detail === "string" ? j.detail : formatApiError({ response: { data: j } }));
      }
      const j = await res.json();
      if (j.reused) toast.success("Já havia um cadastro seu — continuando de onde parou.");
      else toast.success("Cadastro registrado! Envie agora seus documentos.");
      navigate(`/pss/inscricao/${j.invite_token}`);
    } catch (err) { toast.error(err.message); } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-[70vh] flex items-center justify-center text-forest-900/70">
      <Loader2 className="w-5 h-5 animate-spin mr-2" /> Carregando processo seletivo...
    </div>
  );
  if (!proc) return (
    <div className="container mx-auto px-6 py-16 text-center">
      <h1 className="font-display text-3xl">Processo não encontrado</h1>
      <p className="mt-2 text-forest-900/70">Verifique o link recebido.</p>
    </div>
  );

  const disabled = !proc.self_register_enabled;

  return (
    <div className="container mx-auto px-6 py-10 max-w-2xl" data-testid="pss-public-register-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-full bg-forest-700 text-cream-100 flex items-center justify-center">
          <Flame className="w-5 h-5" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-forest-700">{proc.code}</div>
          <h1 className="font-display text-3xl md:text-4xl text-forest-900 leading-tight">
            {proc.title}
          </h1>
        </div>
      </div>

      <div className="bg-cream-100 border border-forest-700/30 p-5 mb-5 text-sm text-forest-900/85">
        <p className="mb-2">
          <b>Auto-cadastro do candidato.</b> Preencha seus dados abaixo. Em seguida, você será
          direcionado para o formulário onde enviará suas declarações, currículo e selfie.
        </p>
        <p className="text-xs text-forest-900/70">
          Prazo final: <b>{proc.deadline}</b>. Total de vagas: <b>{proc.vagas_total || "—"}</b>.
        </p>
      </div>

      {disabled ? (
        <div className="bg-terracotta-500/10 border border-terracotta-500 p-5 text-terracotta-500" data-testid="self-register-disabled">
          Este processo não está com auto-cadastro público habilitado no momento.
        </div>
      ) : (
        <form onSubmit={submit} className="bg-white border border-cream-400 p-6 space-y-5" data-testid="pss-public-form">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-forest-900/70 font-medium">Nome completo *</span>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              data-testid="public-reg-name"
              placeholder="Ex.: Maria Fátima da Silva"
              className="mt-2"
            />
          </label>

          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-forest-900/70 font-medium">Comunidade *</span>
            {Array.isArray(proc.communities) && proc.communities.length > 0 ? (
              <select
                value={form.community}
                onChange={(e) => setForm({ ...form, community: e.target.value })}
                required
                data-testid="public-reg-community"
                className="mt-2 w-full bg-cream-100 border border-cream-400 focus:border-forest-700 focus:outline-none px-3 py-2.5 text-sm text-forest-900"
              >
                <option value="">Selecione sua comunidade...</option>
                {proc.communities.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            ) : (
              <Input
                value={form.community}
                onChange={(e) => setForm({ ...form, community: e.target.value })}
                required
                data-testid="public-reg-community"
                placeholder="Nome da sua comunidade"
                className="mt-2"
              />
            )}
          </label>

          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-forest-900/70 font-medium">Telefone / WhatsApp *</span>
            <Input
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: maskPhone(e.target.value) })}
              required
              data-testid="public-reg-phone"
              placeholder="(93) 99999-9999"
              className="mt-2"
            />
          </label>

          <div className="bg-copaiba-100 border border-copaiba-300 p-3 text-xs text-forest-900">
            Após confirmar, você receberá um link único para enviar suas <b>declarações</b>, foto/selfie e demais documentos exigidos no edital.
          </div>

          <Button
            type="submit"
            disabled={submitting}
            data-testid="public-reg-submit"
            className="w-full bg-forest-700 hover:bg-forest-900 text-cream-100 rounded-none py-6 text-base"
          >
            {submitting ? "Registrando..." : (<span className="inline-flex items-center gap-2">Continuar para envio de documentos <ArrowRight className="w-4 h-4" /></span>)}
          </Button>
        </form>
      )}
    </div>
  );
}
